import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  MagnifyingGlass,
  Ticket,
  FunnelSimple,
  Plus,
  Clock,
  ChatCircleText,
  ArrowRight,
  CaretLeft,
  CaretRight,
} from '@phosphor-icons/react'

import { useLang } from '../../context/LangContext'
import StatusBadge from '../../components/Badge/StatusBadge'

const BASE_TICKETS = [
  {
    title: 'Cannot access HR system after update',
    category: 'IT Support',
    status: 'new',
    priority: 'High',
    latestReply: 'Your request has been received and is waiting for support review.',
  },
  {
    title: 'Network connectivity issue in Block A',
    category: 'IT Support',
    status: 'in_progress',
    priority: 'Medium',
    latestReply: 'Support staff is checking the network equipment in Block A.',
  },
  {
    title: 'Printer not responding on 3rd floor',
    category: 'Hardware',
    status: 'open',
    priority: 'Low',
    latestReply: 'Please share a photo of the printer screen if the issue continues.',
  },
  {
    title: 'Air conditioner not cooling properly',
    category: 'Facilities',
    status: 'resolved',
    priority: 'Medium',
    latestReply: 'Technician has completed the service and marked this ticket as resolved.',
  },
]

const TICKETS = Array.from({ length: 100 }).map((_, index) => {
  const base = BASE_TICKETS[index % BASE_TICKETS.length]
  const no = 100 - index

  return {
    ...base,
    id: String(no).padStart(3, '0'),
    ref: `#TKT-${String(no).padStart(3, '0')}`,
    updatedAgo: index === 0 ? '2 hours ago' : `${index + 1} days ago`,
  }
})

const STATUS_TABS = [
  { key: 'all', labelKey: 'tabAll' },
  { key: 'open', labelKey: 'tabOpen' },
  { key: 'in_progress', labelKey: 'tabInProgress' },
  { key: 'resolved', labelKey: 'tabResolved' },
]

const PAGE_SIZE = 10

function getStatusLabel(t, status) {
  return {
    new: t('statusNew'),
    open: t('statusOpen'),
    in_progress: t('statusInProgress'),
    resolved: t('statusResolved'),
  }[status]
}

function TicketListingCard({ ticket }) {
  const navigate = useNavigate()
  const { t } = useLang()

  return (
    <article
      className="submitted-ticket-card"
      onClick={() => navigate(`/tickets/${ticket.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && navigate(`/tickets/${ticket.id}`)}
    >
      <div className="submitted-ticket-card__icon">
        <Ticket size={24} />
      </div>

      <div className="submitted-ticket-card__body">
        <div className="submitted-ticket-card__top">
          <div>
            <div className="submitted-ticket-card__ref">{ticket.ref}</div>
            <h3>{ticket.title}</h3>
          </div>

          <StatusBadge status={ticket.status} labelOverride={getStatusLabel(t, ticket.status)} />
        </div>

        <div className="submitted-ticket-card__meta">
          <span>{ticket.category}</span>
          <span>{ticket.priority}</span>
          <span><Clock size={14} /> {ticket.updatedAgo}</span>
        </div>

        <div className="submitted-ticket-card__reply">
          <ChatCircleText size={16} />
          <span>{ticket.latestReply}</span>
        </div>
      </div>

      <div className="submitted-ticket-card__arrow">
        <ArrowRight size={18} />
      </div>
    </article>
  )
}

export default function TicketsPage() {
  const { t } = useLang()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('all')
  const [keyword, setKeyword] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const filteredTickets = useMemo(() => {
    return TICKETS.filter((ticket) => {
      const statusMatch = activeTab === 'all' || ticket.status === activeTab
      const keywordMatch = `${ticket.title} ${ticket.category} ${ticket.ref}`
        .toLowerCase()
        .includes(keyword.toLowerCase())

      return statusMatch && keywordMatch
    })
  }, [activeTab, keyword])

  const totalPages = Math.ceil(filteredTickets.length / PAGE_SIZE)
  const startIndex = (currentPage - 1) * PAGE_SIZE
  const currentTickets = filteredTickets.slice(startIndex, startIndex + PAGE_SIZE)

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setCurrentPage(1)
  }

  const handleSearch = (value) => {
    setKeyword(value)
    setCurrentPage(1)
  }

  return (
    <div className="page page--wide submitted-tickets-page">
      <div className="submitted-tickets-hero">
        <div>
          <span className="submitted-tickets-hero__eyebrow">
            <Ticket size={16} /> Ticket history
          </span>
          <h1 className="submitted-tickets-hero__title">{t('trackTickets')}</h1>
          <p className="submitted-tickets-hero__subtitle">{t('trackSubtitle')}</p>
        </div>

        <button className="btn btn--primary" onClick={() => navigate('/tickets/submit')}>
          <Plus size={16} /> {t('submitTicket')}
        </button>
      </div>

      <div className="submitted-tickets-toolbar">
        <div className="submitted-tickets-search">
          <MagnifyingGlass size={18} />
          <input
            value={keyword}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t('searchTickets')}
          />
        </div>

        <button className="btn btn--ghost submitted-tickets-filter">
          <FunnelSimple size={16} /> {t('allStatuses')}
        </button>
      </div>

      <div className="submitted-tickets-tabs">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={activeTab === tab.key ? 'is-active' : ''}
            onClick={() => handleTabChange(tab.key)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      <div className="submitted-ticket-list">
        {currentTickets.map((ticket) => (
          <TicketListingCard key={ticket.id} ticket={ticket} />
        ))}
      </div>

      <div className="pagination-panel">
        <span>
          Showing {filteredTickets.length === 0 ? 0 : startIndex + 1}–
          {Math.min(startIndex + PAGE_SIZE, filteredTickets.length)} of {filteredTickets.length}
        </span>

        <div className="pagination-controls">
          <button
            type="button"
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((page) => page - 1)}
          >
            <CaretLeft size={16} /> Previous
          </button>

          <div className="pagination-pages">
            {Array.from({ length: totalPages }).slice(0, 5).map((_, index) => {
              const page = index + 1

              return (
                <button
                  key={page}
                  type="button"
                  className={currentPage === page ? 'is-active' : ''}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              )
            })}
          </div>

          <button
            type="button"
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((page) => page + 1)}
          >
            Next <CaretRight size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}
